import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  DRAWN_CEILING_RANGE_M,
  applyPlanEdits,
  commentAnchorFor,
  commentTargetAt,
  effectiveRoomGroups,
  planBounds,
  planDistanceM,
  planOrientation,
  planStories,
  planVertices,
  resolveCommentPin,
  scanPlanRooms,
  snapPlanPoint,
  storyLabel,
  turnPlanPoint,
  turnPlanRoom,
  type CommentPin,
  type PlanOrientation,
  type PlanBounds,
  type PlanPoint,
  type PlanSnap,
  type ScanRoomGroup,
} from "@sts/geometry";
import { SelectedReadout } from "@sts/scan-viewer";
import { computeDesignCheck, type RadiatorUpgrade } from "@sts/calc";
import {
  IDENTITY_ZOOM,
  applyZoom,
  fitPlan,
  metresFromView,
  scaleBarMetres,
  toPlan,
  toView,
  viewPointFromClient,
  zoomAbout,
  type PlanInsets,
  type PlanZoom,
} from "./plan-view.js";
import { kindOf, pointsOf } from "./plan-annotations.js";
import { usePlanDimensions, type PlanDimensionsSource } from "./use-plan-dimensions.js";
import { AngleMeasure, AreaMeasure } from "./plan-measure-marks.jsx";
import type { MeasureKind } from "./PlanDimensionsPanel.jsx";
import { editOf, heatLossPlanNotice, withBatch, type PlanEditLayer } from "./use-plan-edits.js";
import { usePlanEditor } from "./use-plan-editor.js";
import { EditHandles, EditToolGhost, EditedMarks } from "./plan-edit-marks.jsx";
import { PlanEditPanel } from "./PlanEditPanel.jsx";
import {
  AutoDimension,
  FloorShapes,
  ManualDimension,
  Opening,
  PlanAxes,
  RoomLabel,
  WallNumber,
  pillBox,
  placePill,
  roomLabelBox,
  type KeepClear,
  type PillPlacement,
  ScaleBar,
  SnapMarker,
  Wall,
} from "./plan-marks.jsx";
import { PlanDimensionsPanel } from "./PlanDimensionsPanel.jsx";
import {
  PlanImageLayer,
  PlanImagePanel,
  planImageCorners,
  rescaledPlacement,
  usePlanImage,
  type PlanImagesSource,
} from "./plan-image.jsx";
import { PlanObjectsPanel } from "./PlanObjectsPanel.jsx";
import { FitFlag, PlanObjectMark, RunLengthTag, vesselNameOffsets } from "./plan-object-marks.jsx";
import {
  buildFitScene,
  checkPlanFit,
  fitOf,
  resizedRadiatorGeometry,
  type FitSuggestion,
} from "./plan-fit.js";
import {
  normaliseDegrees,
  outdoorUnitRotation,
  pipeAnchors,
  placeRadiator,
  runLengthM,
  snapToPipe,
  snappedFittingSpec,
  wallsFacingRooms,
  nearestWall,
  type ObjectTool,
} from "./plan-objects.js";
import {
  isProvisional,
  usePlanObjects,
  type PlanObjectsSource,
} from "./use-plan-objects.js";
import type { HeatPumpDesignInputs, NoiseAssessmentPoint, RadiatorSpec } from "@sts/rules-data";
import { DesignRoomTag, DesignRoomWash, NoisePositionMark } from "./plan-design-marks.jsx";
import { useDesignCheckContext, type DesignCheckSource } from "./use-design-check.js";
import type { NewPlanObject, PlanObject, PlanObjectPatch } from "../api/types.js";
import { usePlanComments, type PlanCommentsSource, type PlanCommentViewer } from "./use-plan-comments.js";
import { PlanCommentsPanel } from "./PlanCommentsPanel.jsx";
import { ReviewNoteList } from "./heat-loss-words.jsx";
import { CommentPinMark } from "./plan-comment-marks.jsx";
import { commentObjects, commentTargetLabel } from "./plan-comments.js";

/**
 * The scan as a drawing.
 *
 * Asked for on 2026-09-09: "make a floor plan of property and keep option over
 * there to add and remove the sizes as well." The 3D model proves the capture
 * is complete; a plan is what a surveyor actually reads — the shape of the
 * property, which wall is which, and how far it is from one corner to another,
 * in one look.
 *
 * SVG, not canvas: text stays crisp at any zoom, every wall is a real element
 * that can take a click, and the whole thing prints.
 *
 * ONE SELECTION, THREE VIEWS. Clicking a wall, opening or floor here calls the
 * same `onSelect` the 3D viewer and the measurement tables already share, and
 * the highlight is driven by the same `selectedId` — so the plan, the model
 * and the table light up together. There is no second selection for scan
 * elements. The one piece of selection state this component owns is which
 * hand-drawn dimension is picked, because those exist nowhere else.
 *
 * EVERY NUMBER COMES FROM THE SAME PLACE. Wall lengths are
 * `ScanElement.widthM` routed through `@sts/geometry`'s projection and printed
 * with `formatLength` — the field and formatter the tables use. See
 * `packages/geometry/src/scan-plan.ts` for why a length is never re-derived
 * from the matrix.
 *
 * TWO KINDS OF DIMENSION. Automatic ones are the scan speaking: one per wall,
 * outside the room, on by default and switchable off when the drawing gets
 * busy. Manual ones are the surveyor speaking: two clicks, snapped, labelled if
 * wanted, removable — and saved in this browser only, which the panel beside
 * them says in words. They are drawn differently on purpose.
 *
 * SIZING. The SVG's viewBox is set to the measured size of its own box, so one
 * drawing unit is one CSS pixel and a 12px label is 12px on screen whatever the
 * plan is fitted to. The component sizes itself (inline styles, not a rule in
 * somebody else's stylesheet) — the lesson of 5cd31e5, where the 3D viewer
 * broke the day the stylesheet around it changed.
 *
 * OBJECTS. Asked for on 2026-09-10: the new heat pump's outdoor unit,
 * radiators, pipe runs and fittings, drawn on the plan by the designer. Unlike
 * the dimensions these are shared design work, saved with the survey through
 * `objects` (see `use-plan-objects.ts`), and the unit and radiators are drawn
 * at their real sizes from the sourced catalogue in `@sts/rules-data`. They
 * are a layout drawing: nothing here feeds heat loss or sizing. The pure
 * placement rules — a radiator flat on a wall, a fitting on a pipe — live in
 * `plan-objects.ts`; how each is drawn, in `plan-object-marks.tsx`.
 *
 * FIT CHECKS. Asked for on 2026-09-10: a radiator too long for its wall, across
 * a door, or too tall for under a window — and an outdoor unit put inside the
 * house — is drawn red with a "!" and listed in the Objects section with the
 * figures it was checked against and, for a radiator, a size that does fit
 * (`plan-fit.ts`). They follow a drag as it happens, so the designer sees a
 * radiator come right as it moves. Advisory only: nothing is refused.
 *
 * MCS DESIGN CHECK. Asked for on 2026-09-10: each room's radiators against its
 * heat loss at the design flow temperature (red when too small), the heat pump
 * size MIS 3005-D asks for and the placed unit against it, and the unit's
 * clearances and MCS 020 a) noise at the neighbour's window. `computeDesignCheck`
 * (@sts/calc) runs here on every change — drags included — over the survey's
 * heat loss and design flow temperature (`design`, see `use-design-check.ts`);
 * the API runs the same function for the installer pack. A room whose
 * radiators are too small is washed red with "Radiators too small" under its
 * name — the fit checks' red, but on the room, so "too small" and "doesn't
 * fit" never look alike. Neighbours' windows are placed with their own tool.
 *
 * ONE TOOL AT A TIME. "Add dimension" and each object tool are the same kind
 * of thing — a pen in the hand — so picking one puts the other down, and
 * Escape puts either down from wherever focus is.
 *
 * EDITING THE PLAN. Asked for on 2026-09-11: fix what the scan got wrong,
 * Polycam-style. The `edits` layer (`use-plan-edits.ts`) holds the designers'
 * corrections; `groups` is already the corrected plan, and while a wall is
 * being dragged the plan draws `applyPlanEdits` over the capture with the drag
 * laid on top, so everything — fit checks, the design check, objects on the
 * moving wall — follows the hand. The gestures and undo live in
 * `use-plan-editor.ts`, the marks in `plan-edit-marks.tsx`, the fields in
 * `PlanEditPanel.tsx`. While "Edit plan" is on the drawing's fit is held
 * still, so moving the outermost wall does not rescale the plan under the
 * cursor. Corrected walls and openings carry a small marker whenever the plan
 * is shown.
 *
 * COMMENTS. Asked for on 2026-09-11 ("option 6"): notes pinned where they
 * apply — "check this radiator position" — for the surveyor, designer and
 * installer (`comments`, see `use-plan-comments.ts`; migration 0043). The
 * Comment tool is one-shot like the others: a click drops the pin (Enter
 * drops it at the pointer, Esc puts the tool down), and the Comments panel
 * opens a box for the words; nothing is posted until Post. A pin dropped on a
 * wall, window, door, room or object is attached to it and drawn relative to
 * it as it is now (`resolveCommentPin`), so it follows a wall being dragged
 * or a radiator being moved. Numbered markers, amber when open; resolved ones
 * only while the panel's resolved list is open.
 *
 * DIMENSIONS are saved with the survey through `dimensions`
 * (`use-plan-dimensions.ts`), moved there from this browser on first load;
 * without one they stay in this browser, as before. Beside a length there is
 * an angle (three clicks) and an area (click round it, Enter to close).
 */

/** Used until the box has been measured, and in environments that cannot. */
const FALLBACK_SIZE = { width: 1000, height: 640 } as const;
/**
 * Clear space around the fitted plan, in px: the view toggle and toolbar
 * along the top (~52px deep), the zoom stack down the right, the scale bar
 * along the bottom — each wide enough that a wall's automatic dimension, drawn
 * ~40px outside it, never runs under a control.
 */
const INSETS: PlanInsets = { top: 88, right: 92, bottom: 64, left: 64 };
/** "Nearest corner within N screen px" — the snapping radius. */
const SNAP_PX = 14;
/** Movement, in px, before a press becomes a pan rather than a click. */
const DRAG_THRESHOLD_PX = 4;
/**
 * Below this width the dimensions panel goes under the drawing instead of
 * beside it. In the three-column survey layout the frame is ~620px wide, and a
 * 260px side column would leave the plan itself a sliver.
 */
const SIDE_PANEL_MIN_WIDTH = 760;
/** How near a wall, in screen px, a dropped radiator goes flat against it. */
const RADIATOR_SNAP_PX = 36;
/** Never less than this in metres, so a zoomed-out plan still snaps a radiator a hand's width off. */
const RADIATOR_SNAP_MIN_M = 0.3;
/** How near a wall, in metres, an outdoor unit turns to face away from it. */
const OUTDOOR_UNIT_ALIGN_M = 2;
/** Rotation steps: R, and Shift+R. */
const ROTATE_STEP_DEG = 90;
const ROTATE_FINE_STEP_DEG = 15;

/**
 * What a hand-drawn plan (0047) adds to the floor plan: the ability to drag
 * out a new room on the grid. The panel beside the plan does everything else
 * — naming, typed wall lengths, rotating, splitting, deleting, storeys —
 * because those are all better as numbers than as gestures.
 */
export interface DrawnRoomLayer {
  /** False for a reader, and for a survey whose plan came from a scan. */
  readonly canDraw: boolean;
  /**
   * A rectangle dragged out on the storey shown, in plan metres. The two
   * points are opposite corners, in the order they were given. The handler
   * decides the room's name, snaps it to its neighbours and saves it.
   */
  readonly onDrawRoom: (storey: number, a: PlanPoint, b: PlanPoint) => void;
  /** The smallest side a dragged room may have, metres — anything less is a stray click. */
  readonly minSideM: number;
  /**
   * The floor-to-ceiling height a dragged room would be given, metres, or
   * null when there is not one yet.
   *
   * A room without a height is not a room — it has no volume, so no heat
   * loss, and `validateDrawnPlan` refuses it on both sides of the wire. The
   * height used to be taken from another room on the storey and fall back to
   * ZERO, which meant the FIRST room on a storey could never be dragged out:
   * the save was refused, silently, every time, and the refusal was written
   * on a panel that full screen does not show ("i cant draw a room",
   * 2026-09-29). The owner resolves the figure; the plan refuses the drag
   * rather than making a room that cannot be saved, and says why.
   */
  readonly newRoomCeilingM: number | null;
  /** That height as the surveyor types it, millimetres, for the field below. */
  readonly ceilingMm: string;
  readonly onCeilingMmChange: (value: string) => void;
}

/** Where a hand-drawn storey with nothing on it yet is drawn: 12 m by 9 m. */
const EMPTY_STOREY_BOUNDS: PlanBounds = { minX: 0, minY: 0, maxX: 12, maxY: 9 };

export interface FloorPlanProps {
  readonly surveyId: string;
  readonly groups: readonly ScanRoomGroup[];
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string | undefined) => void;
  /**
   * Where the plan's objects are kept — the API, in the portal
   * (`planObjectsFromApi`). Without one the Objects section says it is not
   * connected, and the rest of the plan works exactly as before.
   */
  readonly objects?: PlanObjectsSource;
  /**
   * The floor-plan pictures under the storeys (0057): an estate agent's plan
   * to trace over. Without one, the plan has no picture section.
   */
  readonly images?: PlanImagesSource;
  /**
   * Where the MCS design check's context comes from — the survey's heat loss
   * and the job's design flow temperature (`designCheckFromApi`). Without one
   * the check says it is not connected; everything else works as before.
   */
  readonly design?: DesignCheckSource;
  /**
   * The plan's edit layer — the capture, the corrections and the corrected
   * plan (`groups` must be `effectiveRoomGroups` of that plan). Without one
   * the plan is the scan as captured and cannot be edited; corrections made
   * elsewhere are not shown.
   */
  readonly edits?: PlanEditLayer;
  /** Where hand-drawn dimensions are saved — the API. Without one, this browser. */
  readonly dimensions?: PlanDimensionsSource;
  /**
   * Where comments pinned to the plan are kept — the API
   * (`planCommentsFromApi`). Without one there is no Comment tool and no
   * Comments panel.
   */
  readonly comments?: PlanCommentsSource;
  /** Who is looking, for what the Comments panel offers. Without one: may comment, may delete their own. */
  readonly commentViewer?: PlanCommentViewer;
  /**
   * Controls that belong at the top-left of the plan — on the Survey page,
   * the Plan / 3D toggle and the Export menu. They share one flex row with
   * the plan's own tools (top-right), so however wide either side grows the
   * two wrap rather than overlap. Both float over the drawing, so neither
   * moves the drawing's size: the plan never re-fits because of them.
   */
  readonly toolbarLead?: ReactNode;
  /**
   * The plan fills the window (the Survey page's "Full screen"): the side
   * panel is wider, and can be hidden to give the drawing everything.
   */
  readonly fullScreen?: boolean;
  /**
   * Which way up the plan is drawn. It is always squared to its own walls
   * (`@sts/geometry` plan-orientation.ts); `quarterTurns` is the survey's
   * saved choice of which side is the front (0055), null to let the entrance
   * door decide. With `onTurn`, this person may turn it ("Rotate 90°").
   */
  readonly orientation?: {
    readonly quarterTurns: number | null;
    readonly onTurn?: (quarterTurns: number) => void;
  };
  /**
   * Present only on a survey whose plan was DRAWN BY HAND (migration 0047).
   * It adds one thing to the plan: a tool that drags out a new rectangular
   * room on the grid. Everything else a hand-drawn plan needs — doors and
   * windows, radiators, the heat pump, pipes, the cylinder — is the same
   * code as on a scanned plan, because what it is drawing IS a capture.
   */
  readonly drawing?: DrawnRoomLayer;
  /**
   * The storey to show, when something outside needs to agree about it — the
   * hand-drawn plan's room panel, which is edited beside the plan. Left out,
   * the plan keeps its own selection, exactly as it always has.
   */
  readonly storey?: number;
  readonly onStoreyChange?: (storey: number) => void;
  /**
   * May place, move, re-specify and remove the plan's objects and change the
   * design inputs on them — the matrix's `design: update`. False for someone
   * who may read the plan but not change it (an installer on the job): the
   * objects are drawn, listed and selectable; no palette, drag, editor, Delete
   * or R is offered. Defaults to true.
   */
  readonly canEditObjects?: boolean;
  /**
   * May add, label and remove hand-drawn dimensions — `survey: update`, as
   * the API enforces. False: the dimensions are drawn and listed, and the
   * Add dimension / Angle / Area tools are not rendered. Defaults to true.
   */
  readonly canEditDimensions?: boolean;
}

type Mode = "select" | "measure" | "objects" | "position" | "comment";

/** A comment's pin dropped on the plan, waiting for its words. */
interface CommentDraftPin {
  readonly pin: CommentPin;
  readonly about: string | null;
}

/** Points a measurement of each kind is finished at; an area closes on demand. */
const MEASURE_POINTS: Readonly<Record<MeasureKind, number>> = { line: 2, angle: 3, area: Infinity };

/** A neighbour's window being put on the plan for an outdoor unit. */
interface PositionTool {
  readonly unitId: string;
  readonly positionId: string;
  readonly isNew: boolean;
}

interface Drag {
  readonly clientX: number;
  readonly clientY: number;
  /** View units per client pixel, fixed for the duration of the drag. */
  readonly unitsPerPx: number;
  readonly from: PlanZoom;
  moved: boolean;
}

/** An object being dragged: the whole of it, or one point of a pipe run. */
interface ObjectDrag {
  readonly id: string;
  readonly vertexIndex: number | undefined;
  readonly clientX: number;
  readonly clientY: number;
  /** Where the pointer went down, plan metres. */
  readonly start: PlanPoint;
  readonly original: PlanObject;
  moved: boolean;
}

/** An object as it would be if the drag ended now. */
interface DragPreview {
  readonly id: string;
  readonly patch: PlanObjectPatch;
}

function withPatch(object: PlanObject, patch: PlanObjectPatch | undefined): PlanObject {
  if (!patch) return object;
  return {
    ...object,
    ...(patch.spec !== undefined ? { spec: patch.spec } : {}),
    ...(patch.geometry !== undefined ? { geometry: patch.geometry } : {}),
  } as PlanObject;
}

export function FloorPlan({
  surveyId,
  groups,
  selectedId,
  onSelect,
  objects: objectsSource,
  images: imagesSource,
  design: designSource,
  edits,
  dimensions: dimensionsSource,
  comments: commentsSource,
  commentViewer,
  toolbarLead,
  fullScreen = false,
  orientation: orientationChoice,
  drawing,
  storey: controlledStorey,
  onStoreyChange,
  canEditObjects = true,
  canEditDimensions = true,
}: FloorPlanProps): ReactNode {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gridId = `floor-plan-grid-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const altId = `${gridId}-alt`;

  // The plan as saved (the corrected plan when there is an edit layer)...
  const savedRooms = useMemo(() => scanPlanRooms(groups), [groups]);
  const stories = useMemo(() => planStories(savedRooms), [savedRooms]);
  // The names the rest of the app calls the storeys, for the per-storey emitter
  // panel — so it says "Loft conversion" where the storey list does. Memoised
  // because it is a dependency of that panel's own memo, and a fresh Map every
  // render would rebuild the roll-up on every mouse move.
  const storeyNames = useMemo(
    () => new Map(stories.map((s) => [s.story, s.label] as const)),
    [stories],
  );

  const [story, setStory] = useState<number | null>(null);
  // A storey chosen outside wins, so the plan and the panel beside it can
  // never be looking at different floors of the same house.
  const chosen = controlledStorey ?? story;
  const activeStory =
    chosen !== null && chosen !== undefined && stories.some((s) => s.story === chosen)
      ? chosen
      : (stories[0]?.story ?? 0);
  const chooseStorey = (next: number): void => {
    setStory(next);
    onStoreyChange?.(next);
  };
  const savedVisible = useMemo(
    () => savedRooms.filter((room) => room.story === activeStory),
    [savedRooms, activeStory],
  );
  // Squared to its walls, the front at the bottom along X: worked out from
  // every storey, so all of them turn together.
  const chosenTurns = orientationChoice?.quarterTurns ?? null;
  const liveOrientation = useMemo(
    () => planOrientation(savedRooms, chosenTurns),
    [savedRooms, chosenTurns],
  );

  // The picture under this storey, and the two points picked to set its scale.
  const planImage = usePlanImage(imagesSource, activeStory);
  const [scalePoints, setScalePoints] = useState<readonly PlanPoint[] | null>(null);
  useEffect(() => setScalePoints(null), [activeStory]);

  const planObjects = usePlanObjects(
    objectsSource,
    surveyId,
    commentViewer ? { meId: commentViewer.userId } : {},
  );
  // Filled in below, once the drawing's fit is known; the editor reads them at event time.
  const planAtRef = useRef<(clientX: number, clientY: number) => PlanPoint | null>(() => null);
  const snapRadiusRef = useRef(0.2);
  const swallowClickRef = useRef(false);
  const editor = usePlanEditor({
    layer: edits,
    rooms: savedVisible,
    objects: planObjects.objects,
    updateObject: planObjects.update,
    activeStory,
    selectedId,
    onSelect,
    planAt: (x, y) => planAtRef.current(x, y),
    snapRadiusM: () => snapRadiusRef.current,
    swallowNextClick: () => {
      swallowClickRef.current = true;
    },
    onToolArmed: () => {
      setMode("select");
      setTool(null);
      setRun([]);
      setPendingPoints([]);
      setHover(null);
      setCursor(null);
      setPositionTool(null);
    },
  });
  const planComments = usePlanComments(commentsSource, surveyId);

  // ...and as drawn: with a wall drag in progress laid over it, so the
  // walls, the openings in them, the fit checks and the design check all
  // follow the hand before anything is saved.
  const previewGroups = useMemo(() => {
    if (!edits || !editor.preview) return null;
    const merged = withBatch(edits.state.records, editor.preview.batch, false).map(editOf);
    return effectiveRoomGroups(applyPlanEdits(edits.capture, merged));
  }, [edits, editor.preview]);
  const drawnGroups = previewGroups ?? groups;
  const rooms = useMemo(
    () => (previewGroups ? scanPlanRooms(previewGroups) : savedRooms),
    [previewGroups, savedRooms],
  );

  const visible = useMemo(
    () => rooms.filter((room) => room.story === activeStory),
    [rooms, activeStory],
  );
  // A wall between two rooms of one split capture is listed in both, under
  // one id (room-split.ts): drawn, and dimensioned, once — by the first.
  const drawnBy = useMemo(() => {
    const by = new Map<string, (typeof visible)[number]>();
    for (const room of visible) for (const s of room.segments) if (!by.has(s.id)) by.set(s.id, room);
    return by;
  }, [visible]);
  // The turn, held while editing for the same reason the fit is (below): a
  // saved wall that shifts the main wall direction must not turn the drawing
  // under the cursor. A deliberate "Rotate 90°" still turns it.
  const [frozenOrientation, setFrozenOrientation] = useState<PlanOrientation | undefined>(undefined);
  useEffect(() => {
    setFrozenOrientation(editor.editing ? liveOrientation : undefined);
    // liveOrientation deliberately omitted, as liveBounds is below.
  }, [editor.editing, chosenTurns]);
  const orientation = (editor.editing && frozenOrientation) || liveOrientation;
  const turnDeg = orientation.turnDeg;
  // The picture counts towards what the plan is fitted to: on a storey with
  // no rooms drawn yet it is the whole of it.
  const pictureCorners = useMemo(
    () =>
      planImage.image && !planImage.image.hidden
        ? planImageCorners(planImage.image).map((p) => turnPlanPoint(p, turnDeg))
        : [],
    [planImage.image, turnDeg],
  );
  const liveBounds = useMemo(() => {
    const rooms = planBounds(savedVisible.map((room) => turnPlanRoom(room, turnDeg)));
    if (pictureCorners.length === 0) return rooms;
    const xs = pictureCorners.map((p) => p.x);
    const ys = pictureCorners.map((p) => p.y);
    const picture = { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
    return rooms
      ? {
          minX: Math.min(rooms.minX, picture.minX),
          minY: Math.min(rooms.minY, picture.minY),
          maxX: Math.max(rooms.maxX, picture.maxX),
          maxY: Math.max(rooms.maxY, picture.maxY),
        }
      : picture;
  }, [savedVisible, turnDeg, pictureCorners]);
  // While editing, the fit is held where it was when editing began (per
  // storey): a wall moved out past the edge must not rescale the plan under
  // the cursor. It is fitted afresh when editing ends.
  const [frozenBounds, setFrozenBounds] = useState<PlanBounds | undefined>(undefined);
  useEffect(() => {
    setFrozenBounds(editor.editing ? liveBounds : undefined);
    // liveBounds deliberately omitted: an edit must not move the frozen fit.
  }, [editor.editing, activeStory, turnDeg]);
  // A hand-drawn storey with nothing on it yet still needs somewhere to draw:
  // without a bounds there is no fit, and without a fit a pointer has no plan
  // position, so the first room could never be dragged out. Only ever used
  // while a room can be drawn and nothing has been — a scanned plan, and a
  // drawn one with rooms on it, are untouched.
  const bounds =
    (editor.editing && frozenBounds ? frozenBounds : liveBounds) ??
    (drawing?.canDraw ? EMPTY_STOREY_BOUNDS : undefined);
  const vertices = useMemo(() => planVertices(visible), [visible]);
  const walls = useMemo(
    () => visible.flatMap((room) => room.segments.filter((s) => s.kind === "wall")),
    [visible],
  );
  /** The same walls, each knowing which side is its room — what a radiator is put against. */
  const roomWalls = useMemo(() => wallsFacingRooms(visible), [visible]);
  /** What the fit checks measure against: this storey's walls, openings and floor levels. */
  const fitScene = useMemo(() => buildFitScene(visible), [visible]);

  const [size, setSize] = useState<{ width: number; height: number }>(FALLBACK_SIZE);
  const [wide, setWide] = useState(true);
  const [zoom, setZoom] = useState<PlanZoom>(IDENTITY_ZOOM);
  const [mode, setMode] = useState<Mode>("select");
  const [showWallSizes, setShowWallSizes] = useState(true);
  // "Wall 3, Room 2" is how the tables, the checks and the heat loss name a
  // wall: this puts the number on the plan, so a wall can be found by it.
  const [showWallNumbers, setShowWallNumbers] = useState(true);
  // Full screen only: the side panel folded away, the drawing given the lot.
  // Hidden rather than unmounted, so what is typed in it survives.
  const [panelHidden, setPanelHidden] = useState(false);
  // Hidden rather than unmounted (see `panelHidden`), and available embedded
  // as well as full screen: the drawing is the point of the screen and the
  // panel is a third of it.
  const sideHidden = panelHidden;
  /** The measurement in hand: what it measures, and the points put down so far. */
  const [measureKind, setMeasureKind] = useState<MeasureKind>("line");
  const [pendingPoints, setPendingPoints] = useState<readonly PlanPoint[]>([]);
  const pending = pendingPoints[0] ?? null;
  const [hover, setHover] = useState<PlanSnap | null>(null);
  const dims = usePlanDimensions(dimensionsSource, surveyId);
  const dimensions = dims.dimensions;
  const [selectedDimensionId, setSelectedDimensionId] = useState<string | null>(null);

  const [tool, setTool] = useState<ObjectTool | null>(null);
  /** Where the pointer is, plan metres, while an object tool is in hand. */
  const [cursor, setCursor] = useState<PlanPoint | null>(null);
  /** The pipe run being drawn: the points laid so far. */
  const [run, setRun] = useState<readonly PlanPoint[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const design = useDesignCheckContext(designSource, surveyId);
  const [positionTool, setPositionTool] = useState<PositionTool | null>(null);
  const [commentDraft, setCommentDraft] = useState<CommentDraftPin | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const measuring = mode === "measure";
  const commenting = mode === "comment";
  const commentsOn = commentsSource !== undefined && planComments.status !== "unsupported";
  const mayComment = commentsOn && commentViewer?.canComment !== false;
  const placing = mode === "objects" && tool !== null;
  const editTool = editor.tool !== null;
  const toolActive = mode !== "select" || editTool;

  // ------------------------------------------------------------ storage ----

  const dropDimension = useCallback(
    (id: string) => {
      dims.remove(id);
      setSelectedDimensionId((current) => (current === id ? null : current));
    },
    [dims],
  );

  // ------------------------------------------------------------- layout ----

  // A hand-drawn storey with nothing on it yet is still a plan: it is the
  // empty grid the first room is dragged out on.
  const hasDrawing = bounds !== undefined && (visible.length > 0 || drawing?.canDraw === true);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const wrapper = wrapperRef.current;
    if (!stage || !wrapper) return;
    const measure = (): void => {
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      // A box laid out at zero (hidden, or an environment with no layout)
      // keeps the fallback rather than fitting the plan into nothing.
      if (width > 0 && height > 0) {
        setSize((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }
      if (wrapper.clientWidth > 0) setWide(wrapper.clientWidth >= SIDE_PANEL_MIN_WIDTH);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [hasDrawing]);

  const baseFit = useMemo(
    () => (bounds ? fitPlan(bounds, size.width, size.height, INSETS, turnDeg) : null),
    [bounds, size, turnDeg],
  );
  const fit = useMemo(() => (baseFit ? applyZoom(baseFit, zoom) : null), [baseFit, zoom]);

  // Turned a quarter: a pan made on the old way up means nothing on the new.
  useEffect(() => {
    setZoom(IDENTITY_ZOOM);
  }, [orientation.quarterTurns]);

  // A different storey is a different drawing: start it fitted.
  useEffect(() => {
    setZoom(IDENTITY_ZOOM);
    setPendingPoints([]);
    setHover(null);
    setRun([]);
    setCursor(null);
    setSelectedObjectId(null);
  }, [activeStory]);

  // Every object as it would be if the drag in progress ended now — its own
  // drag, or a wall drag it is following.
  const liveObjects = useMemo(() => {
    const following = editor.preview?.objects;
    if (!dragPreview && !following?.size) return planObjects.objects;
    return planObjects.objects.map((object) => {
      if (dragPreview && dragPreview.id === object.id) return withPatch(object, dragPreview.patch);
      const geometry = following?.get(object.id);
      return geometry ? withPatch(object, { geometry }) : object;
    });
  }, [planObjects.objects, dragPreview, editor.preview]);
  // This storey's objects, likewise.
  const storeyObjects = useMemo(
    () => liveObjects.filter((object) => object.storey === activeStory),
    [liveObjects, activeStory],
  );
  // What a fitting snaps onto. A run still being saved counts: its points are
  // known, and a designer who drops an elbow straight after finishing a run
  // means that run.
  const storeyPipes = useMemo(
    () =>
      storeyObjects.filter(
        (object): object is Extract<PlanObject, { kind: "pipe" }> => object.kind === "pipe",
      ),
    [storeyObjects],
  );
  const selectedObject = storeyObjects.find((object) => object.id === selectedObjectId) ?? null;
  // Every radiator's and the unit's fit — recomputed as a drag moves, because
  // `storeyObjects` already carries the drag's preview. Sizes that fit are
  // searched for only for the selected object: the one the panel shows them for.
  const fits = useMemo(
    () =>
      checkPlanFit(storeyObjects, visible, fitScene, {
        suggestFor: new Set(selectedObjectId ? [selectedObjectId] : []),
      }),
    [storeyObjects, visible, fitScene, selectedObjectId],
  );
  // Every storey's objects as they would be if the drag ended now — the design
  // check is whole-house (the heat pump is sized against every room).
  const allObjects = liveObjects;
  // `previewContext` — the saved design conditions with the flow-temperature
  // slider's figures over the top, and equal to the saved ones when nothing is
  // being previewed. So moving the slider re-rates every emitter on the plan
  // at once, which is the whole point of it, while everything that WRITES a
  // figure stamped with the design flow temperature reads `design.context`
  // (the saved one) instead — see `use-design-check.ts`.
  const designCheck = useMemo(
    () =>
      design.previewContext
        ? computeDesignCheck({ context: design.previewContext, rooms, objects: allObjects })
        : undefined,
    [design.previewContext, rooms, allObjects],
  );
  const roomChecks = useMemo(
    () => new Map((designCheck?.rooms.rooms ?? []).map((r) => [`${r.story}:${r.index}`, r])),
    [designCheck],
  );

  // An object that has gone — removed here, rolled back, removed elsewhere —
  // cannot stay selected. A provisional id is left alone: `addObject` hands
  // the selection on to the saved id itself, or drops it if the save failed.
  useEffect(() => {
    if (
      selectedObjectId &&
      !isProvisional(selectedObjectId) &&
      !planObjects.objects.some((o) => o.id === selectedObjectId)
    ) {
      setSelectedObjectId(null);
    }
  }, [planObjects.objects, selectedObjectId]);

  // A comment deleted — here or by a colleague — cannot stay selected.
  useEffect(() => {
    if (selectedCommentId && !planComments.threads.some((t) => t.id === selectedCommentId)) {
      setSelectedCommentId(null);
    }
  }, [planComments.threads, selectedCommentId]);

  // A selection made elsewhere — the tables, the 3D view, the rooms list — on
  // a room that is on another storey brings that storey up, so the highlight
  // is never on a drawing the surveyor cannot see.
  useEffect(() => {
    if (!selectedId) return;
    const owner = rooms.find(
      (room) =>
        room.segments.some((s) => s.id === selectedId) || room.floorIds.includes(selectedId),
    );
    if (owner && owner.story !== activeStory) setStory(owner.story);
    // activeStory deliberately omitted: switching storeys by hand must not be
    // undone by a selection that has not changed.
  }, [selectedId, rooms]);

  // -------------------------------------------------------------- input ----

  // The room being dragged out, while the pointer is down with the room tool
  // armed. Plan metres; null at rest.
  const [roomDrag, setRoomDrag] = useState<{ from: PlanPoint; to: PlanPoint } | null>(null);
  const [roomTool, setRoomTool] = useState(false);
  /** Why the last drag made no room. Cleared as soon as one can be made. */
  const [roomRefusal, setRoomRefusal] = useState<string | null>(null);
  const canDrawRooms = drawing?.canDraw === true;

  const viewPointOf = useCallback(
    (clientX: number, clientY: number): PlanPoint | undefined => {
      const svg = svgRef.current;
      if (!svg) return undefined;
      return viewPointFromClient(
        svg.getBoundingClientRect(),
        clientX,
        clientY,
        size.width,
        size.height,
      );
    },
    [size],
  );

  /** A browser position as plan metres, snapped to a corner or wall when near one. */
  const snapAt = useCallback(
    (clientX: number, clientY: number): PlanSnap | null => {
      if (!fit) return null;
      const view = viewPointOf(clientX, clientY);
      if (!view) return null;
      return snapPlanPoint(toPlan(view, fit), vertices, walls, metresFromView(SNAP_PX, fit));
    },
    [fit, viewPointOf, vertices, walls],
  );

  /** A browser position as plan metres, exactly where it is. */
  const planAt = useCallback(
    (clientX: number, clientY: number): PlanPoint | null => {
      if (!fit) return null;
      const view = viewPointOf(clientX, clientY);
      return view ? toPlan(view, fit) : null;
    },
    [fit, viewPointOf],
  );
  planAtRef.current = planAt;
  snapRadiusRef.current = fit ? metresFromView(SNAP_PX, fit) : 0.2;

  /**
   * A pipe point: snapped to another run's corner or end, or a fitting, as a
   * corner — so runs join up — then to the plan's corners and walls, as a
   * dimension end is.
   */
  const snapPipePoint = useCallback(
    (point: PlanPoint, excludeId?: string): PlanSnap | null => {
      if (!fit) return null;
      const anchors = pipeAnchors(storeyObjects.filter((o) => o.id !== excludeId));
      return snapPlanPoint(point, [...anchors, ...vertices], walls, metresFromView(SNAP_PX, fit));
    },
    [fit, storeyObjects, vertices, walls],
  );

  const radiatorRadiusM = fit
    ? Math.max(metresFromView(RADIATOR_SNAP_PX, fit), RADIATOR_SNAP_MIN_M)
    : RADIATOR_SNAP_MIN_M;
  const fittingRadiusM = fit ? metresFromView(SNAP_PX * 1.5, fit) : 0;

  /** What a click at `point` would put down with `tool` in hand. Not pipes: they take several clicks. */
  const placementAt = useCallback(
    (held: ObjectTool, point: PlanPoint): NewPlanObject | null => {
      switch (held.kind) {
        case "heat-pump":
          return {
            kind: "heat-pump",
            storey: activeStory,
            spec: held.spec,
            geometry: {
              x: point.x,
              y: point.y,
              rotationDeg: outdoorUnitRotation(point, roomWalls, OUTDOOR_UNIT_ALIGN_M) ?? 0,
            },
          };
        case "radiator":
          return {
            kind: "radiator",
            storey: activeStory,
            spec: held.spec,
            geometry: placeRadiator(
              point,
              held.spec.lengthMm / 1000,
              held.spec.depthMm / 1000,
              roomWalls,
              radiatorRadiusM,
            ).geometry,
          };
        case "fitting": {
          const on = snapToPipe(point, storeyPipes, fittingRadiusM);
          return on
            ? {
                kind: "fitting",
                storey: activeStory,
                // The pipe's size and material; a reducer where two sizes meet takes both.
                spec: snappedFittingSpec(held.spec, on),
                geometry: { x: on.point.x, y: on.point.y, rotationDeg: on.rotationDeg },
              }
            : {
                kind: "fitting",
                storey: activeStory,
                spec: held.spec,
                geometry: { x: point.x, y: point.y, rotationDeg: 0 },
              };
        }
        case "cylinder":
          return { kind: "cylinder", storey: activeStory, spec: held.spec, geometry: { x: point.x, y: point.y, rotationDeg: 0 } };
        case "buffer":
          return { kind: "buffer", storey: activeStory, spec: held.spec, geometry: { x: point.x, y: point.y, rotationDeg: 0 } };
        case "pipe":
          return null;
      }
    },
    [activeStory, roomWalls, radiatorRadiusM, storeyPipes, fittingRadiusM],
  );

  function addObject(input: NewPlanObject): void {
    const { provisionalId, saved } = planObjects.add(input);
    setSelectedObjectId(provisionalId);
    setSelectedDimensionId(null);
    onSelect(undefined);
    void saved.then((object) =>
      setSelectedObjectId((current) =>
        current === provisionalId ? (object?.id ?? null) : current,
      ),
    );
  }

  function finishRun(): void {
    if (tool?.kind !== "pipe") return;
    // Enter keeps the leg the designer can see. The rubber band runs from the
    // last clicked point to the (snapped) cursor, and Josh drew one leg that
    // way, pressed Enter, and watched it vanish: only clicked points counted,
    // so a one-click run was thrown away (2026-09-10). A double-click's
    // second click lands on the cursor already, so this adds nothing then.
    const end = hover?.point;
    const last = run[run.length - 1];
    const points = end && last && planDistanceM(last, end) >= 0.01 ? [...run, end] : run;
    if (points.length >= 2 && runLengthM(points) >= 0.01) {
      addObject({ kind: "pipe", storey: activeStory, spec: tool.spec, geometry: { points } });
    }
    setRun([]);
    armTool(null);
  }

  /** Picks up an object tool, or puts it down (null). Either way the dimension pen goes down. */
  function armTool(next: ObjectTool | null): void {
    setPendingPoints([]);
    setHover(null);
    if (!next) {
      setTool(null);
      setRun([]);
      setCursor(null);
      setMode((current) => (current === "objects" ? "select" : current));
      return;
    }
    setPositionTool(null);
    editor.armTool(null);
    // Changing the pipe size mid-run keeps the run; any other change starts afresh.
    setRun((current) => (next.kind === "pipe" && tool?.kind === "pipe" ? current : []));
    setTool(next);
    setMode("objects");
  }

  function rotateObject(id: string, stepDeg: number): void {
    const object = storeyObjects.find((o) => o.id === id);
    if (!object || object.kind === "pipe") return;
    planObjects.update(id, {
      geometry: {
        ...object.geometry,
        rotationDeg: normaliseDegrees(object.geometry.rotationDeg + stepDeg),
      },
    });
  }

  function applySuggestion(id: string, suggestion: FitSuggestion): void {
    planObjects.update(id, { spec: suggestion.spec, geometry: suggestion.geometry });
  }

  /**
   * A placed radiator's spec changed in the panel. A new size or type keeps
   * its back on the wall and its place along it; a new mounting height moves
   * nothing on the plan.
   */
  function changeRadiatorSpec(id: string, spec: RadiatorSpec): void {
    const object = storeyObjects.find((o) => o.id === id);
    if (!object || object.kind !== "radiator") return;
    const resized =
      spec.panelType !== object.spec.panelType ||
      spec.heightMm !== object.spec.heightMm ||
      spec.lengthMm !== object.spec.lengthMm;
    planObjects.update(
      id,
      resized ? { spec, geometry: resizedRadiatorGeometry(object, spec, fitScene) } : { spec },
    );
  }

  function applyUpgrade(upgrade: RadiatorUpgrade): void {
    planObjects.update(upgrade.objectId, { spec: upgrade.spec, geometry: upgrade.geometry });
  }

  function changeHeatPumpDesign(id: string, next: HeatPumpDesignInputs): void {
    const object = planObjects.objects.find((o) => o.id === id);
    if (!object || object.kind !== "heat-pump") return;
    const { design: _old, ...rest } = object.spec;
    const spec = Object.keys(next).length > 0 ? { ...rest, design: next } : rest;
    planObjects.update(id, { spec });
  }

  /** Puts the neighbour's-window pen in the hand (or down again, for the same one). */
  function startPlacingPosition(unitId: string, positionId: string, isNew: boolean): void {
    if (positionTool && positionTool.positionId === positionId) {
      setPositionTool(null);
      setMode("select");
      setCursor(null);
      return;
    }
    setTool(null);
    setRun([]);
    setPendingPoints([]);
    setHover(null);
    editor.armTool(null);
    setPositionTool({ unitId, positionId, isNew });
    setMode("position");
  }

  function placePosition(point: PlanPoint): void {
    const held = positionTool;
    setPositionTool(null);
    setMode("select");
    setCursor(null);
    if (!held) return;
    const unit = planObjects.objects.find((o) => o.id === held.unitId);
    if (!unit || unit.kind !== "heat-pump") return;
    const current = unit.spec.design ?? {};
    const positions = current.noisePositions ?? [];
    const placed = { x: point.x, y: point.y };
    const next: NoiseAssessmentPoint[] = held.isNew
      ? [
          ...positions,
          {
            id: held.positionId,
            description: "",
            point: placed,
            distanceM: null,
            barrierType: "none",
            lineOfSight: "full-view",
          },
        ]
      : positions.map((p) => (p.id === held.positionId ? { ...p, point: placed } : p));
    planObjects.update(unit.id, { spec: { ...unit.spec, design: { ...current, noisePositions: next } } });
  }

  /** A room picked from the design check: its storey up, its floor lit. */
  function pickRoom(roomStory: number, index: number): void {
    const room = rooms.find((r) => r.story === roomStory && r.index === index);
    if (!room) return;
    setStory(roomStory);
    const floorId = room.floorIds[0];
    setSelectedObjectId(null);
    setSelectedDimensionId(null);
    if (floorId) onSelect(floorId);
  }

  function removeObject(id: string): void {
    planObjects.remove(id);
    setSelectedObjectId((current) => (current === id ? null : current));
  }

  const dragRef = useRef<Drag | null>(null);
  const objectDragRef = useRef<ObjectDrag | null>(null);
  const dragPreviewRef = useRef<DragPreview | null>(null);

  /** Where a dragged object would land, the pointer having moved `delta` metres. */
  function dragPatch(drag: ObjectDrag, delta: PlanPoint): PlanObjectPatch {
    const o = drag.original;
    switch (o.kind) {
      case "pipe": {
        const points = o.geometry.points;
        const i = drag.vertexIndex;
        if (i !== undefined && points[i]) {
          const moved = { x: points[i].x + delta.x, y: points[i].y + delta.y };
          const landed = snapPipePoint(moved, o.id)?.point ?? moved;
          return { geometry: { points: points.map((p, j) => (j === i ? landed : p)) } };
        }
        return {
          geometry: { points: points.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y })) },
        };
      }
      case "radiator":
        return {
          geometry: placeRadiator(
            { x: o.geometry.x + delta.x, y: o.geometry.y + delta.y },
            o.spec.lengthMm / 1000,
            o.spec.depthMm / 1000,
            roomWalls,
            radiatorRadiusM,
            o.geometry.rotationDeg,
          ).geometry,
        };
      case "heat-pump":
        return {
          geometry: { ...o.geometry, x: o.geometry.x + delta.x, y: o.geometry.y + delta.y },
        };
      case "fitting": {
        const at = { x: o.geometry.x + delta.x, y: o.geometry.y + delta.y };
        const on = snapToPipe(at, storeyPipes, fittingRadiusM);
        return on
          ? {
              geometry: { x: on.point.x, y: on.point.y, rotationDeg: on.rotationDeg },
              spec: snappedFittingSpec(o.spec, on),
            }
          : { geometry: { ...o.geometry, ...at } };
      }
      case "cylinder":
      case "buffer":
        return {
          geometry: { ...o.geometry, x: o.geometry.x + delta.x, y: o.geometry.y + delta.y },
        };
    }
  }

  // Read by the window listeners below, which are registered once.
  const objectDragHandlers = useRef({
    move: (_event: PointerEvent): void => {},
    up: (): void => {},
    cancel: (): void => {},
  });
  objectDragHandlers.current = {
    move(event) {
      const drag = objectDragRef.current;
      if (!drag) return;
      // No button held means the release was never reported to us — the
      // browser took the gesture over (a native drag, a trackpad gesture,
      // a release outside the window) and sent pointercancel or nothing.
      // Without this the object stayed glued to the cursor with nothing
      // able to put it down (Josh, 2026-09-10: "the pipe is still stuck
      // whatever I do"). Treat it as the release it was.
      if (event.buttons === 0) {
        objectDragHandlers.current.up();
        return;
      }
      const dx = event.clientX - drag.clientX;
      const dy = event.clientY - drag.clientY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      const at = planAt(event.clientX, event.clientY);
      if (!at) return;
      const preview = {
        id: drag.id,
        patch: dragPatch(drag, { x: at.x - drag.start.x, y: at.y - drag.start.y }),
      };
      dragPreviewRef.current = preview;
      setDragPreview(preview);
    },
    up() {
      const drag = objectDragRef.current;
      const preview = dragPreviewRef.current;
      objectDragRef.current = null;
      dragPreviewRef.current = null;
      if (!drag) return;
      if (drag.moved) {
        // A drag is not a click: it must not also deselect where it ended.
        swallowClickRef.current = true;
        if (preview) planObjects.update(drag.id, preview.patch);
      }
      setDragPreview(null);
    },
    /** Escape mid-drag: put the object back where it was. */
    cancel() {
      if (!objectDragRef.current) return;
      objectDragRef.current = null;
      dragPreviewRef.current = null;
      swallowClickRef.current = true;
      setDragPreview(null);
    },
  };

  useEffect(() => {
    const move = (event: PointerEvent): void => objectDragHandlers.current.move(event);
    const up = (): void => objectDragHandlers.current.up();
    // Enter drops the object where it is; Escape puts it back. Only while a
    // drag is actually live, so these keys mean nothing else is pre-empted.
    const key = (event: KeyboardEvent): void => {
      if (!objectDragRef.current) return;
      if (event.key === "Enter") {
        event.preventDefault();
        objectDragHandlers.current.up();
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        objectDragHandlers.current.cancel();
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    // A cancelled pointer (the browser took the gesture) and a window that
    // lost focus mid-drag both end the drag where it stands.
    window.addEventListener("pointercancel", up);
    window.addEventListener("blur", up);
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("blur", up);
      window.removeEventListener("keydown", key, true);
    };
  }, []);

  /** A press on an object: select it, and be ready to drag it (or one point of a pipe). */
  function onObjectPointerDown(
    id: string,
    event: ReactPointerEvent<SVGElement>,
    vertexIndex?: number,
  ): void {
    if (event.button !== 0 || isProvisional(id)) return;
    // Not a pan: the press belongs to the object.
    event.stopPropagation();
    swallowClickRef.current = false;
    const original = planObjects.objects.find((o) => o.id === id);
    const start = planAt(event.clientX, event.clientY);
    if (!original || !start) return;
    setSelectedObjectId(id);
    setSelectedDimensionId(null);
    setSelectedCommentId(null);
    onSelect(undefined);
    // Selected, to read about; never picked up by someone who may not move it.
    if (!canEditObjects) return;
    objectDragRef.current = {
      id,
      vertexIndex,
      clientX: event.clientX,
      clientY: event.clientY,
      start,
      original,
      moved: false,
    };
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>): void {
    swallowClickRef.current = false;
    if (event.button !== 0) return;
    // Drawing a room owns the press outright: no pan, no placement, no
    // selection — the drag IS the room.
    if (roomTool && canDrawRooms) {
      const at = planAt(event.clientX, event.clientY);
      if (at) {
        setRoomDrag({ from: at, to: at });
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      return;
    }
    // With a tool in hand a press is always a placement, never a pan. A
    // trackpad click that wandered past the 4 px pan threshold used to be
    // taken as a pan and its click swallowed, so the object stayed on the
    // pointer and "it's not allowing me to drop it at any point" (Josh,
    // 2026-09-10). Zoom (wheel, +/−, Fit) still works while drawing.
    if (toolActive) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = Math.min(rect.width / size.width, rect.height / size.height);
    dragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      unitsPerPx: scale > 0 ? 1 / scale : 1,
      from: zoom,
      moved: false,
    };
  }

  // Pan listens on the window so a drag that leaves the drawing keeps going
  // rather than sticking, the same as the 3D viewer's orbit.
  useEffect(() => {
    const onMove = (event: PointerEvent): void => {
      const drag = dragRef.current;
      if (!drag) return;
      // Same missed-release guard as the object drag above.
      if (event.buttons === 0) {
        onUp();
        return;
      }
      const dx = event.clientX - drag.clientX;
      const dy = event.clientY - drag.clientY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      setZoom({
        scale: drag.from.scale,
        panX: drag.from.panX + dx * drag.unitsPerPx,
        panY: drag.from.panY + dy * drag.unitsPerPx,
      });
    };
    const onUp = (): void => {
      const drag = dragRef.current;
      dragRef.current = null;
      // A drag is not a click: without this, panning the plan would drop a
      // dimension end or clear the selection where the drag finished.
      if (drag?.moved) swallowClickRef.current = true;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  // Wheel zoom about the cursor. A native listener because React's wheel
  // handler is passive and cannot stop the page scrolling underneath.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (event: WheelEvent): void => {
      const anchor = viewPointOf(event.clientX, event.clientY);
      if (!anchor) return;
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.0015);
      setZoom((current) => zoomAbout(current, factor, anchor));
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [viewPointOf, hasDrawing]);

  function zoomBy(factor: number): void {
    setZoom((current) =>
      zoomAbout(current, factor, { x: size.width / 2, y: size.height / 2 }),
    );
  }

  function onClickCapture(event: ReactMouseEvent<SVGSVGElement>): void {
    if (swallowClickRef.current) {
      swallowClickRef.current = false;
      event.stopPropagation();
    }
  }

  /**
   * A click that no mark took. Measuring, it lays a dimension down in two
   * clicks; otherwise it clears the selection, because clicking the empty part
   * of a drawing means "nothing", not "keep whatever was lit".
   */
  function onBackgroundClick(event: ReactMouseEvent<SVGSVGElement>): void {
    if (editTool) {
      editor.onToolClick(event.clientX, event.clientY);
      return;
    }
    if (mode === "position" && positionTool) {
      const point = planAt(event.clientX, event.clientY);
      if (point) placePosition(point);
      return;
    }
    if (commenting) {
      const point = planAt(event.clientX, event.clientY);
      if (point) dropCommentAt(point);
      return;
    }
    if (placing && tool) {
      const point = planAt(event.clientX, event.clientY);
      if (!point) return;
      if (tool.kind === "pipe") {
        const snap = snapPipePoint(point);
        if (!snap) return;
        const last = run[run.length - 1];
        // The second click of a double-click lands on the first: a slip, not a leg.
        if (last && planDistanceM(last, snap.point) < 0.01) return;
        setRun([...run, snap.point]);
        return;
      }
      dropObjectAt(point);
      return;
    }
    if (!measuring) {
      // A click on an object or a pin was handled already; it is not "nothing".
      if ((event.target as Element).closest?.("[data-object-id], [data-comment-id]")) return;
      onSelect(undefined);
      setSelectedDimensionId(null);
      setSelectedObjectId(null);
      setSelectedCommentId(null);
      return;
    }
    const snap = snapAt(event.clientX, event.clientY);
    if (!snap) return;
    const first = pendingPoints[0];
    // An area closes on a click back on its first point.
    if (
      measureKind === "area" &&
      first &&
      pendingPoints.length >= 3 &&
      fit &&
      planDistanceM(first, snap.point) <= metresFromView(SNAP_PX, fit)
    ) {
      finishMeasure(pendingPoints);
      return;
    }
    const last = pendingPoints[pendingPoints.length - 1];
    // A double-click on one spot is a slip, not a zero-length leg.
    if (last && planDistanceM(last, snap.point) < 0.01) return;
    const next = [...pendingPoints, snap.point];
    if (next.length >= MEASURE_POINTS[measureKind]) finishMeasure(next);
    else setPendingPoints(next);
  }

  /**
   * Lays the object in hand down at `point` and puts the tool down, so
   * nothing is left following the pointer. One object per pick — pick the
   * tool again for the next one.
   */
  function dropObjectAt(point: PlanPoint): void {
    if (!tool || tool.kind === "pipe") return;
    const input = placementAt(tool, point);
    if (input) addObject(input);
    armTool(null);
  }

  /** Saves the measurement in hand through `points`, then puts the pen down. */
  function finishMeasure(points: readonly PlanPoint[]): void {
    // Never save a measurement that has no real position to stand on.
    if (!points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return;
    const legsOk = points.every((p, i) => i === 0 || planDistanceM(points[i - 1] as PlanPoint, p) >= 0.01);
    const enough = measureKind === "area" ? points.length >= 3 : points.length === MEASURE_POINTS[measureKind];
    if (legsOk && enough) {
      const id = dims.add(activeStory, measureKind, points);
      setSelectedDimensionId(id);
    }
    setPendingPoints([]);
    setHover(null);
    setMode("select");
  }

  /** Enter: the point on the pointer goes down as the last one, and the pen with it. */
  function dropDimensionEnd(end: PlanPoint): void {
    const last = pendingPoints[pendingPoints.length - 1];
    const points = last && planDistanceM(last, end) < 0.01 ? pendingPoints : [...pendingPoints, end];
    finishMeasure(points);
  }

  /**
   * The end of a room drag. A rectangle smaller than the layer's minimum on
   * either side is dropped rather than added: it is a stray click on the
   * plan, and a 40 mm room is never what anybody meant.
   */
  function onRoomDragEnd(): void {
    const drag = roomDrag;
    setRoomDrag(null);
    if (!drag || !drawing) return;
    const w = Math.abs(drag.to.x - drag.from.x);
    const d = Math.abs(drag.to.y - drag.from.y);
    swallowClickRef.current = true;

    // TOO SMALL, AND IT SAYS SO. This used to `return` without a word, which
    // made a single CLICK on the plan — the obvious thing to try — do nothing
    // whatsoever, with no hint that a drag was wanted. Worse, it ran BEFORE
    // the height check below, so somebody clicking never even learned a
    // height was needed ("trying to draw rooms and everything here but not
    // working", 2026-10-07, on a survey with a floor plan to trace over).
    //
    // A click and a too-small drag need different advice, so they get it: one
    // is the wrong gesture, the other is the right gesture at the wrong size.
    if (w < drawing.minSideM || d < drawing.minSideM) {
      const mm = Math.round(drawing.minSideM * 1000);
      const barelyMoved = w < 0.02 && d < 0.02;
      setRoomRefusal(
        barelyMoved
          ? "A room is DRAGGED, not clicked: press and hold at one corner, " +
              "pull to the opposite corner, then let go."
          : `That rectangle is under ${mm} mm on one side, which is not a room. ` +
              "Drag out a bigger one.",
      );
      return;
    }
    // No height, no room. Said here, on the plan, where the drag happened —
    // the panel that carries the drawing's other messages is beside the plan,
    // and full screen does not show it.
    if (drawing.newRoomCeilingM === null) {
      setRoomRefusal(
        `Type the floor-to-ceiling height first — between ${DRAWN_CEILING_RANGE_M.min} m and ` +
          `${DRAWN_CEILING_RANGE_M.max} m. It is the room's volume, so nothing here guesses it.`,
      );
      return;
    }
    setRoomRefusal(null);
    drawing.onDrawRoom(activeStory, drag.from, drag.to);
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>): void {
    if (roomDrag) {
      const at = planAt(event.clientX, event.clientY);
      if (at) setRoomDrag((drag) => (drag ? { ...drag, to: at } : drag));
      return;
    }
    if (dragRef.current?.moved || objectDragRef.current) return;
    if (editTool) {
      editor.onToolMove(event.clientX, event.clientY);
      return;
    }
    if (measuring) {
      setHover(snapAt(event.clientX, event.clientY));
      return;
    }
    if (mode === "position" || commenting) {
      setCursor(planAt(event.clientX, event.clientY));
      return;
    }
    if (!placing || !tool) return;
    const point = planAt(event.clientX, event.clientY);
    setCursor(point);
    if (tool.kind === "pipe") setHover(point ? snapPipePoint(point) : null);
  }

  function onDoubleClick(): void {
    if (placing && tool?.kind === "pipe") finishRun();
    // A double-click closes an area: its clicks have already put the corners down.
    else if (measuring && measureKind === "area" && pendingPoints.length >= 3) finishMeasure(pendingPoints);
  }

  /** Picks up the measuring pen for `kind` — or puts it down, if that is the one in hand. */
  function toggleMeasuring(kind: MeasureKind = "line"): void {
    setPositionTool(null);
    editor.armTool(null);
    const putDown = mode === "measure" && measureKind === kind;
    setMode(putDown ? "select" : "measure");
    setMeasureKind(kind);
    setPendingPoints([]);
    setHover(null);
    setTool(null);
    setRun([]);
    setCursor(null);
  }

  // Escape while drawing — a dimension, or with an object tool in hand — is
  // heard wherever focus is. Handled on the plan's own box only, it did
  // nothing after a click that left focus on the page body — the surveyor had
  // to find "Done" to back out of a half-drawn line. Only while a tool is in
  // hand: at rest the page's other Escape handlers (the photo lightbox) must
  // not be pre-empted. Enter finishes a pipe run the same way.
  const windowKeys = useRef((_event: KeyboardEvent): void => {});
  windowKeys.current = (event) => {
    const target = event.target as Element | null;
    if (target?.closest?.("input, textarea, select")) return;
    // The plan editor's tools follow the same two rules (use-plan-editor.ts).
    if (editTool) {
      editor.onToolKey(event);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (measuring) {
        if (pendingPoints.length > 0) setPendingPoints([]);
        else {
          setMode("select");
          setHover(null);
        }
      } else if (mode === "position") {
        setPositionTool(null);
        setMode("select");
        setCursor(null);
      } else if (commenting) {
        setMode("select");
        setCursor(null);
      } else if (run.length > 0) {
        setRun([]);
      } else {
        armTool(null);
      }
      return;
    }
    // Enter drops whatever is on the pointer, where the pointer is, and puts
    // the tool down: the dimension's last point, the neighbour's window, the
    // object in hand, the pipe run. With nothing to drop, Enter just puts the
    // tool down — nothing is ever left stuck to the pointer.
    if (event.key === "Enter") {
      event.preventDefault();
      if (measuring) {
        if (pending && hover) dropDimensionEnd(hover.point);
        else if (measureKind === "area" && pendingPoints.length >= 3) finishMeasure(pendingPoints);
        else {
          setPendingPoints([]);
          setHover(null);
          setMode("select");
        }
      } else if (mode === "position") {
        if (cursor) placePosition(cursor);
        else {
          setPositionTool(null);
          setMode("select");
          setCursor(null);
        }
      } else if (commenting) {
        if (cursor) dropCommentAt(cursor);
        else {
          setMode("select");
          setCursor(null);
        }
      } else if (tool?.kind === "pipe") {
        finishRun();
      } else if (tool && cursor) {
        dropObjectAt(cursor);
      } else {
        armTool(null);
      }
    }
  };
  useEffect(() => {
    if (!toolActive) return;
    const onKey = (event: KeyboardEvent): void => windowKeys.current(event);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toolActive]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    const target = event.target as Element;
    // Typing a label must never delete the dimension it labels.
    if (target.closest("input, textarea, select")) return;
    // With "Edit plan" on: Delete the selected wall or opening, undo, redo, straighten.
    if (!toolActive && editor.onKey(event)) return;

    if (event.key === "Escape") {
      // A tool in hand is handled by the window listener above.
      if (!toolActive) {
        setSelectedDimensionId(null);
        setSelectedObjectId(null);
        setSelectedCommentId(null);
      }
      return;
    }
    if ((event.key === "Delete" || event.key === "Backspace") && selectedDimensionId && canEditDimensions) {
      event.preventDefault();
      dropDimension(selectedDimensionId);
      return;
    }
    if (
      (event.key === "Delete" || event.key === "Backspace") &&
      selectedObjectId &&
      canEditObjects &&
      !isProvisional(selectedObjectId)
    ) {
      event.preventDefault();
      removeObject(selectedObjectId);
      return;
    }
    if (
      (event.key === "r" || event.key === "R") &&
      selectedObjectId &&
      canEditObjects &&
      !event.metaKey &&
      !event.ctrlKey
    ) {
      event.preventDefault();
      rotateObject(selectedObjectId, event.shiftKey ? ROTATE_FINE_STEP_DEG : ROTATE_STEP_DEG);
      return;
    }
    if (target === svgRef.current) {
      if (event.key === "+" || event.key === "=") zoomBy(1.25);
      else if (event.key === "-" || event.key === "_") zoomBy(0.8);
      else if (event.key === "0") setZoom(IDENTITY_ZOOM);
    }
  }

  /** "Edit plan" on or off. On, every other tool goes down and objects step back. */
  function toggleEditing(): void {
    const on = !editor.editing;
    editor.setEditing(on);
    if (on) {
      setMode("select");
      setTool(null);
      setRun([]);
      setPendingPoints([]);
      setHover(null);
      setCursor(null);
      setPositionTool(null);
      setSelectedObjectId(null);
      setSelectedDimensionId(null);
    }
  }

  function pickElement(id: string): void {
    onSelect(selectedId === id ? undefined : id);
    setSelectedDimensionId(null);
    setSelectedObjectId(null);
    setSelectedCommentId(null);
  }

  function pickDimension(id: string | null): void {
    setSelectedDimensionId(id);
    if (id !== null) {
      onSelect(undefined);
      setSelectedObjectId(null);
      setSelectedCommentId(null);
    }
  }

  function pickObject(id: string | null): void {
    setSelectedObjectId(id);
    if (id !== null) {
      setSelectedDimensionId(null);
      onSelect(undefined);
      setSelectedCommentId(null);
    }
  }

  /** A comment picked on the plan or in the panel: its storey up, its pin lit. */
  function pickComment(id: string | null): void {
    setSelectedCommentId(id);
    if (id === null) return;
    setSelectedDimensionId(null);
    setSelectedObjectId(null);
    const thread = planComments.threads.find((t) => t.id === id);
    if (!thread) return;
    if (thread.status === "resolved") setShowResolved(true);
    if (thread.pin.storey !== activeStory && stories.some((s) => s.story === thread.pin.storey)) {
      setStory(thread.pin.storey);
    }
  }

  /** Picks up the Comment tool — or puts it down, if it is in hand. Every other tool goes down. */
  function toggleCommenting(): void {
    const putDown = commenting;
    setPositionTool(null);
    editor.armTool(null);
    setTool(null);
    setRun([]);
    setPendingPoints([]);
    setHover(null);
    setCursor(null);
    setMode(putDown ? "select" : "comment");
  }

  /**
   * Drops the comment's pin at `point` and puts the tool down: on whatever is
   * under it (an object, then a window or door, a wall, the room's floor),
   * attached so it follows that element. The words come next, in the panel.
   */
  function dropCommentAt(point: PlanPoint): void {
    setMode("select");
    setCursor(null);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    const objects = commentObjects(storeyObjects);
    const radius = fit ? metresFromView(SNAP_PX, fit) : 0.2;
    const target = commentTargetAt(point, visible, objects, radius);
    const anchor = target ? commentAnchorFor(point, target, visible, objects) : null;
    setCommentDraft({
      pin: {
        storey: activeStory,
        x: point.x,
        y: point.y,
        ...(target && anchor ? { target, anchor } : {}),
      },
      about: target && anchor ? commentTargetLabel(target, savedRooms, planObjects.objects) : null,
    });
    setSelectedCommentId(null);
  }

  async function postComment(body: string): Promise<boolean> {
    const draft = commentDraft;
    if (!draft) return false;
    const saved = await planComments.create({ body, pin: draft.pin });
    if (!saved) return false;
    setCommentDraft(null);
    setSelectedCommentId(saved.id);
    return true;
  }

  // ------------------------------------------------------------- render ----

  const mine = dimensions.filter((dimension) => dimension.story === activeStory);
  const activeStoryLabel = stories.find((s) => s.story === activeStory)?.label ?? storyLabel(activeStory);

  if (!fit || !hasDrawing) {
    return (
      <div className="floor-plan floor-plan--empty" style={{ padding: 24 }}>
        <p className="empty">
          {drawing
            ? "Nothing has been drawn on this storey yet."
            : `There is no positioned geometry in this scan to draw a plan from. The
          measurements below are unaffected — they come from each surface's
          own dimensions, which do not need a position.`}
        </p>
      </div>
    );
  }

  const bar = scaleBarMetres(fit, size.width / 4);
  // A metre grid while a metre is big enough to see; five metres below that.
  const gridStep = fit.pixelsPerMetre >= 24 ? 1 : 5;
  const gridPx = gridStep * fit.pixelsPerMetre;
  const interactive = !toolActive;
  // With "Edit plan" on, a press on a wall or opening belongs to the editor
  // (its own targets, drawn on top), and the objects and dimensions step back.
  const editing = editor.editing;
  const markInteractive = interactive && !editing;
  // What a click would put down, drawn under the cursor before it is.
  const ghost: PlanObject | null = (() => {
    if (!placing || !tool) return null;
    const identity = {
      id: "ghost",
      surveyId,
      label: null,
      createdBy: null,
      updatedBy: null,
      createdAt: "",
      updatedAt: "",
    };
    if (tool.kind === "pipe") {
      const points = hover && run.length > 0 ? [...run, hover.point] : run;
      if (points.length < 2) return null;
      return { ...identity, kind: "pipe", storey: activeStory, spec: tool.spec, geometry: { points } };
    }
    if (!cursor) return null;
    const input = placementAt(tool, cursor);
    return input ? ({ ...identity, ...input, label: null } as PlanObject) : null;
  })();
  /** Which way a radiator's label goes: into its room, off the wall it is on. */
  const labelToward = (object: PlanObject): PlanPoint | undefined => {
    if (object.kind !== "radiator") return undefined;
    const reach = object.spec.depthMm / 2000 + 0.05;
    return nearestWall({ x: object.geometry.x, y: object.geometry.y }, roomWalls, reach)?.wall.inward;
  };
  // The ghost is checked too, so a radiator too long for the wall under the
  // cursor says so before it is put down.
  const ghostFit = ghost ? fitOf(ghost, [...storeyObjects, ghost], fitScene, { suggest: false }) : undefined;
  const ghostToward = ghost ? labelToward(ghost) : undefined;
  // Comments' pins on this storey, each where its element is NOW — the
  // drawn rooms and objects carry any drag in progress, so a pin on a wall
  // being dragged goes with the hand. Resolved ones only while listed.
  const commentObjs = commentObjects(storeyObjects);
  const pinsHere = planComments.threads
    .filter(
      (t) =>
        t.pin.storey === activeStory && (t.status === "open" || showResolved || t.id === selectedCommentId),
    )
    .map((thread) => ({ thread, placed: resolveCommentPin(thread.pin, visible, commentObjs) }))
    // The selected pin last, so it is drawn on top of any it overlaps.
    .sort((a, b) => Number(a.thread.id === selectedCommentId) - Number(b.thread.id === selectedCommentId));
  const storeyLabelOf = (storey: number): string | null =>
    stories.length > 1 ? (stories.find((s) => s.story === storey)?.label ?? storyLabel(storey)) : null;
  const drawOrder: Record<PlanObject["kind"], number> = {
    pipe: 0,
    radiator: 1,
    "heat-pump": 1,
    cylinder: 1,
    buffer: 1,
    fitting: 2,
  };
  const drawn = [...storeyObjects].sort((a, b) => drawOrder[a.kind] - drawOrder[b.kind]);
  // A cylinder's and a buffer's names kept apart when the two sit side by
  // side — the ghost of one being put down beside the other included.
  const vesselNames = vesselNameOffsets(ghost ? [...drawn, ghost] : drawn, fit);
  // Figures are placed one after another, each keeping clear of the rooms'
  // names and of every figure placed before it.
  const keepClear: KeepClear[] = visible.map((room) => roomLabelBox(room, fit));
  const pills = new Map<string, PillPlacement>();
  for (const dimension of mine) {
    if (kindOf(dimension) !== "line") continue;
    const pill = placePill(dimension, fit, keepClear);
    if (pill) {
      pills.set(dimension.id, pill);
      keepClear.push(pillBox(pill));
    }
  }

  return (
    <div
      ref={wrapperRef}
      className="floor-plan"
      data-testid="floor-plan"
      onKeyDown={onKeyDown}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: wide ? "row" : "column",
        width: "100%",
        height: "100%",
        minHeight: 360,
      }}
    >
      <div
        ref={stageRef}
        className="floor-plan__stage"
        style={{ position: "relative", flex: "1 1 auto", minWidth: 0, minHeight: 0 }}
      >
        <svg
          ref={svgRef}
          className={toolActive ? "floor-plan__svg is-measuring" : "floor-plan__svg"}
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          // Never "from the scan" for a plan nobody scanned: the accessible
          // name is a claim about where the geometry came from, like every
          // other one this feature had to correct.
          aria-label={
            drawing
              ? `Floor plan of ${activeStoryLabel}, drawn by hand`
              : `Floor plan of ${activeStoryLabel}, drawn from the scan`
          }
          aria-describedby={altId}
          tabIndex={0}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
            cursor: toolActive ? "crosshair" : "grab",
            touchAction: "none",
            userSelect: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onRoomDragEnd}
          onPointerCancel={() => setRoomDrag(null)}
          onPointerLeave={() => {
            setHover(null);
            setCursor(null);
            editor.onToolLeave();
          }}
          onClickCapture={onClickCapture}
          onClick={onBackgroundClick}
          onDoubleClick={onDoubleClick}
        >
          <defs>
            <pattern
              id={gridId}
              patternUnits="userSpaceOnUse"
              width={gridPx}
              height={gridPx}
              x={fit.offsetX}
              y={fit.offsetY}
            >
              <path
                className="floor-plan__grid-line"
                d={`M ${gridPx} 0 L 0 0 0 ${gridPx}`}
                fill="none"
              />
            </pattern>
          </defs>
          <rect className="floor-plan__paper" width={size.width} height={size.height} />
          <rect width={size.width} height={size.height} fill={`url(#${gridId})`} />
          {planImage.image && planImage.url && !planImage.image.hidden ? (
            <PlanImageLayer image={planImage.image} url={planImage.url} fit={fit} />
          ) : null}

          {visible.map((room) => (
            <FloorShapes
              key={`floor-${room.index}`}
              room={room}
              fit={fit}
              selectedId={selectedId}
              interactive={interactive}
              onPick={pickElement}
            />
          ))}

          {visible.map((room) => {
            const check = roomChecks.get(`${room.story}:${room.index}`);
            return check ? <DesignRoomWash key={`wash-${room.index}`} room={room} fit={fit} check={check} /> : null;
          })}

          {visible.map((room) =>
            room.segments
              .filter((segment) => segment.kind === "wall" && drawnBy.get(segment.id) === room)
              .map((segment) => (
                <Wall
                  key={segment.id}
                  segment={segment}
                  fit={fit}
                  selected={segment.id === selectedId}
                  interactive={markInteractive}
                  onPick={pickElement}
                />
              )),
          )}

          {visible.map((room) =>
            room.segments
              .filter((segment) => segment.kind !== "wall" && drawnBy.get(segment.id) === room)
              .map((segment) => (
                <Opening
                  key={segment.id}
                  segment={segment}
                  room={room}
                  fit={fit}
                  selected={segment.id === selectedId}
                  interactive={markInteractive}
                  onPick={pickElement}
                />
              )),
          )}

          {/* Walls and openings the designers corrected: a small marker by
              each, whenever the plan is shown, with who and when. */}
          {edits ? (
            <EditedMarks rooms={visible} fit={fit} status={edits.plan.status} records={edits.state.records} />
          ) : null}

          {visible.map((room) => (
            <RoomLabel key={`label-${room.index}`} room={room} fit={fit} />
          ))}

          {showWallNumbers
            ? visible.map((room) =>
                room.segments
                  .filter((segment) => segment.kind === "wall")
                  .map((segment) => (
                    <WallNumber
                      key={`num-${room.index}-${segment.id}`}
                      segment={segment}
                      room={room}
                      fit={fit}
                      selected={segment.id === selectedId}
                    />
                  )),
              )
            : null}

          {visible.map((room) => {
            const check = roomChecks.get(`${room.story}:${room.index}`);
            return check ? <DesignRoomTag key={`tag-${room.index}`} room={room} fit={fit} check={check} /> : null;
          })}

          {showWallSizes
            ? visible.map((room) =>
                room.segments
                  .filter((segment) => segment.kind === "wall" && drawnBy.get(segment.id) === room)
                  .map((segment) => (
                    <AutoDimension
                      key={`dim-${segment.id}`}
                      segment={segment}
                      room={room}
                      fit={fit}
                      selected={segment.id === selectedId}
                    />
                  )),
              )
            : null}

          {/* The designer's objects: pipes under radiators and the outdoor
              unit, fittings on top of the pipes they sit on. Above the walls,
              because a radiator is against a wall and must not vanish into
              its stroke; under the dimensions, which measure them. */}
          <g className="floor-plan__objects" data-testid="plan-objects-layer">
            {drawn.map((object) => (
              <PlanObjectMark
                key={object.id}
                object={object}
                fit={fit}
                selected={object.id === selectedObjectId}
                interactive={markInteractive}
                onPointerDown={onObjectPointerDown}
                {...(object.kind === "radiator" ? { labelToward: labelToward(object) } : {})}
                {...(fits.has(object.id) ? { fitCheck: fits.get(object.id) } : {})}
                {...(vesselNames.has(object.id) ? { nameDy: vesselNames.get(object.id) } : {})}
              />
            ))}
          </g>

          {/* Neighbours' windows (MCS 020 a) assessment positions), each tied
              to the unit it is measured from, with the level there. */}
          <g className="floor-plan__noise-layer" data-testid="plan-noise-layer">
            {storeyObjects.map((object) => {
              if (object.kind !== "heat-pump") return null;
              const outcome = designCheck?.outdoorUnits.find((u) => u.objectId === object.id);
              return (object.spec.design?.noisePositions ?? []).map((position) =>
                position.point ? (
                  <NoisePositionMark
                    key={`${object.id}-${position.id}`}
                    from={{ x: object.geometry.x, y: object.geometry.y }}
                    at={position.point}
                    fit={fit}
                    {...(outcome?.noise.positions.find((r) => r.id === position.id)
                      ? { result: outcome.noise.positions.find((r) => r.id === position.id) }
                      : {})}
                    {...(position.description ? { label: position.description } : {})}
                  />
                ) : null,
              );
            })}
            {mode === "position" && positionTool && cursor
              ? (() => {
                  const unit = storeyObjects.find((o) => o.id === positionTool.unitId);
                  return unit && unit.kind === "heat-pump" ? (
                    <NoisePositionMark
                      from={{ x: unit.geometry.x, y: unit.geometry.y }}
                      at={cursor}
                      fit={fit}
                      label={`${planDistanceM({ x: unit.geometry.x, y: unit.geometry.y }, cursor).toFixed(1)} m`}
                      ghost
                    />
                  ) : null;
                })()
              : null}
          </g>

          {/* Fit warnings, above every object so none hides another's: a "!"
              on every flagged one, and the words on the selected one. */}
          <g className="floor-plan__fit-flags" data-testid="plan-fit-layer">
            {drawn.map((object) => {
              const check = fits.get(object.id);
              if (!check || check.problems.length === 0) return null;
              const toward = labelToward(object);
              return (
                <FitFlag
                  key={`fit-${object.id}`}
                  object={object}
                  fit={fit}
                  fitCheck={check}
                  showTag={object.id === selectedObjectId}
                  {...(toward ? { toward } : {})}
                />
              );
            })}
          </g>

          {mine.map((dimension) => {
            const kind = kindOf(dimension);
            if (kind === "line") {
              return (
                <ManualDimension
                  key={dimension.id}
                  dimension={dimension}
                  fit={fit}
                  selected={dimension.id === selectedDimensionId}
                  interactive={markInteractive}
                  onPick={pickDimension}
                  {...(canEditDimensions ? { onRemove: dropDimension } : {})}
                  {...(pills.has(dimension.id) ? { placement: pills.get(dimension.id) } : {})}
                />
              );
            }
            const props = {
              id: dimension.id,
              points: pointsOf(dimension),
              ...(dimension.label ? { label: dimension.label } : {}),
              fit,
              selected: dimension.id === selectedDimensionId,
              interactive: markInteractive,
              onPick: pickDimension,
              ...(canEditDimensions ? { onRemove: dropDimension } : {}),
            };
            return kind === "angle" ? (
              <AngleMeasure key={dimension.id} {...props} />
            ) : (
              <AreaMeasure key={dimension.id} {...props} />
            );
          })}

          {/* The rubber band: the measurement being drawn, following the
              cursor with its live figure, so it is visible before the last
              click commits it rather than after. */}
          {measuring && pending && hover ? (
            measureKind === "line" ? (
              <ManualDimension
                dimension={{ id: "pending", start: pending, end: hover.point }}
                fit={fit}
                pendingBand
                keepClear={keepClear}
              />
            ) : measureKind === "angle" ? (
              <AngleMeasure id="pending" points={[...pendingPoints, hover.point]} fit={fit} pending />
            ) : (
              <AreaMeasure id="pending" points={[...pendingPoints, hover.point]} fit={fit} pending />
            )
          ) : null}

          {/* The object a click would put down, or the pipe run so far with
              its rubber band to the cursor and its length as it stands. */}
          {ghost ? (
            <g data-testid="plan-object-ghost">
              <PlanObjectMark
                object={ghost}
                fit={fit}
                ghost
                {...(ghost.kind === "radiator" ? { labelToward: ghostToward } : {})}
                {...(ghostFit ? { fitCheck: ghostFit } : {})}
                {...(vesselNames.has(ghost.id) ? { nameDy: vesselNames.get(ghost.id) } : {})}
              />
              {ghostFit && ghostFit.problems.length > 0 ? (
                <FitFlag
                  object={ghost}
                  fit={fit}
                  fitCheck={ghostFit}
                  showTag
                  {...(ghostToward ? { toward: ghostToward } : {})}
                />
              ) : null}
            </g>
          ) : null}
          {placing && tool?.kind === "pipe" && run.length > 0 ? (
            <RunLengthTag
              points={hover ? [...run, hover.point] : run}
              fit={fit}
              at={hover?.point ?? (run[run.length - 1] as PlanPoint)}
            />
          ) : null}
          {placing && tool?.kind === "pipe"
            ? run.map((point, i) => <SnapMarker key={`run-${i}`} point={point} kind="corner" fit={fit} />)
            : null}

          {pendingPoints.map((point, i) => (
            <SnapMarker key={`measure-${i}`} point={point} kind="corner" fit={fit} />
          ))}
          {(measuring || (placing && tool?.kind === "pipe") || editor.tool?.kind === "wall" || editor.tool?.kind === "split") && (hover ?? editor.hover) ? (
            <SnapMarker
              point={(hover ?? editor.hover)!.point}
              kind={(hover ?? editor.hover)!.kind}
              fit={fit}
            />
          ) : null}

          {/* Comments' pins, over the drawing and its figures: a note must
              never hide under a caption. Under the editor's handles, which
              take a press on a wall while editing. */}
          {commentsOn ? (
            <g className="floor-plan__comments-layer" data-testid="plan-comments-layer">
              {pinsHere.map(({ thread, placed }) => (
                <CommentPinMark
                  key={thread.id}
                  id={thread.id}
                  at={toView(placed.point, fit)}
                  number={thread.number}
                  look={thread.status === "open" ? "open" : "resolved"}
                  selected={thread.id === selectedCommentId}
                  detached={placed.state === "detached"}
                  interactive={markInteractive}
                  label={`Comment ${thread.number}: ${thread.body.slice(0, 80)}`}
                  onPick={(id) => pickComment(selectedCommentId === id ? null : id)}
                />
              ))}
              {commentDraft && commentDraft.pin.storey === activeStory ? (
                <CommentPinMark
                  id="draft"
                  at={toView(resolveCommentPin(commentDraft.pin, visible, commentObjs).point, fit)}
                  number={null}
                  look="draft"
                />
              ) : null}
              {commenting && cursor ? (
                <g data-testid="plan-comment-ghost">
                  <CommentPinMark id="ghost" at={toView(cursor, fit)} number={null} look="draft" />
                </g>
              ) : null}
            </g>
          ) : null}

          {/* The plan editor: press targets and handles over the walls, and
              what its tools would put down. On top, so a press is the
              editor's. */}
          {/* Only with no other tool in hand: a click with the measuring pen or
              an object in hand is that tool's, even on a wall. */}
          {editing && mode === "select" ? (
            <EditHandles rooms={visible} fit={fit} editor={editor} selectedId={selectedId} />
          ) : null}
          {editTool ? (
            <>
              {editor.pendingStart ? <SnapMarker point={editor.pendingStart} kind="corner" fit={fit} /> : null}
              <EditToolGhost
                fit={fit}
                rooms={visible}
                pendingStart={editor.pendingStart}
                hoverPoint={editor.hover?.point ?? null}
                ghost={editor.ghost}
              />
            </>
          ) : null}

          {/* Setting the picture's scale: two clicks on it, caught here above
              everything else so no tool takes them. */}
          {scalePoints !== null ? (
            <g data-testid="plan-picture-scale">
              {scalePoints.map((p, i) => {
                const v = toView(p, fit);
                return <circle key={i} className="plan-picture__point" cx={v.x} cy={v.y} r={6} />;
              })}
              {scalePoints.length === 2 ? (
                <line
                  className="plan-picture__span"
                  x1={toView(scalePoints[0]!, fit).x}
                  y1={toView(scalePoints[0]!, fit).y}
                  x2={toView(scalePoints[1]!, fit).x}
                  y2={toView(scalePoints[1]!, fit).y}
                />
              ) : (
                <rect
                  data-testid="plan-picture-scale-catch"
                  width={size.width}
                  height={size.height}
                  fill="transparent"
                  style={{ cursor: "crosshair" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    const p = planAt(event.clientX, event.clientY);
                    if (p) setScalePoints((pts) => [...(pts ?? []), p]);
                  }}
                />
              )}
            </g>
          ) : null}

          <ScaleBar fit={fit} metres={bar} right={size.width - 20} bottom={size.height - 20} />
          <PlanAxes x={size.width - 20 - bar * fit.pixelsPerMetre - 58} y={size.height - 16} />
          {/* Which side is the front, once it is known — the entrance door, or
              somebody's "Rotate 90°". Under the building, along X. */}
          {orientation.front !== "none" && bounds ? (
            <text
              className="floor-plan__front"
              data-testid="plan-front"
              x={Math.round(((bounds.minX + bounds.maxX) / 2) * fit.pixelsPerMetre + fit.offsetX)}
              y={Math.round(bounds.maxY * fit.pixelsPerMetre + fit.offsetY + 46)}
              style={{ pointerEvents: "none" }}
            >
              FRONT
            </text>
          ) : null}
        </svg>

        <p id={altId} className="visually-hidden">
          A scale drawing of {activeStoryLabel} seen from above, with each wall labelled
          with its measured length. Every figure on it is also listed in the
          Walls, Openings and Floors tables below, which are the text version of
          this drawing. Dimensions you add are listed with the plan, where each
          can be labelled or removed; with a dimension selected, Delete
          removes it. Objects — the outdoor unit, radiators, pipes and
          fittings — are listed in the Objects section beside the plan; with
          one selected, R rotates it and Delete removes it. A radiator or
          outdoor unit that does not fit where it is drawn is marked on the
          plan and listed there with the reason. Edit plan corrects walls,
          doors, windows and room names; the selected element&apos;s figures
          can be typed in the Edit plan panel, and Delete, Ctrl+Z and
          Ctrl+Shift+Z delete, undo and redo. Comment pins a numbered note
          where it applies; every comment is listed, with its replies, in the
          Comments section beside the plan.
        </p>

        {/* One row across the top of the plan: the lead (Plan / 3D, Export)
            at the left, the plan's tools at the right. A flex row that wraps,
            so the two can never overlap — at a narrow width the tools drop
            under the lead instead. It floats over the drawing (absolute), so
            wrapping never changes the drawing's size or re-fits the plan. */}
        {roomDrag ? (
          <div
            className="floor-plan__room-drag"
            data-testid="floor-plan-room-drag"
            style={{
              position: "absolute",
              left: Math.min(toView(roomDrag.from, fit).x, toView(roomDrag.to, fit).x),
              top: Math.min(toView(roomDrag.from, fit).y, toView(roomDrag.to, fit).y),
              width: Math.abs(toView(roomDrag.to, fit).x - toView(roomDrag.from, fit).x),
              height: Math.abs(toView(roomDrag.to, fit).y - toView(roomDrag.from, fit).y),
              border: "2px dashed currentColor",
              pointerEvents: "none",
            }}
          />
        ) : null}

        {roomRefusal ? (
          <p className="floor-plan__refusal" role="status" data-testid="floor-plan-room-refusal">
            {roomRefusal}
          </p>
        ) : null}

        <div className="floor-plan__topbar" data-testid="floor-plan-topbar">
          {toolbarLead ? (
            <div className="floor-plan__lead" data-testid="floor-plan-lead">
              {toolbarLead}
            </div>
          ) : null}
          <div className="floor-plan__toolbar" role="toolbar" aria-label="Plan tools">
            {stories.length > 1 ? (
              <select
                className="floor-plan__story"
                aria-label="Storey"
                value={activeStory}
                onChange={(event) => chooseStorey(Number(event.target.value))}
              >
                {stories.map((entry) => (
                  <option key={entry.story} value={entry.story}>
                    {entry.label}
                  </option>
                ))}
              </select>
            ) : null}

            {canDrawRooms ? (
              <button
                type="button"
                className="floor-plan__tool floor-plan__tool--primary"
                aria-pressed={roomTool}
                onClick={() => {
                  setRoomRefusal(null);
                  setRoomTool((on) => !on);
                }}
                title="Drag out a rectangle on the plan to add a room"
              >
                {roomTool ? "Done" : "Draw a room"}
              </button>
            ) : null}

            {/* The height goes with the drag, not beside the plan: in full
                screen the panel that asks for it is not on the page at all,
                and without a height the drag can do nothing. Shown only while
                the tool is in hand, and only until the storey has a room to
                take the figure from. */}
            {canDrawRooms && roomTool && drawing && drawing.newRoomCeilingM === null ? (
              <label className="floor-plan__ceiling">
                Floor to ceiling (mm)
                <input
                  inputMode="numeric"
                  aria-label="Floor to ceiling height for a new room, in millimetres"
                  value={drawing.ceilingMm}
                  onChange={(event) => {
                    setRoomRefusal(null);
                    drawing.onCeilingMmChange(event.target.value);
                  }}
                />
              </label>
            ) : null}

            <button
              type="button"
              className="floor-plan__tool"
              aria-pressed={showWallSizes}
              onClick={() => setShowWallSizes((on) => !on)}
              title="Show or hide the measured length of every wall"
            >
              Wall sizes
            </button>
            <button
              type="button"
              className="floor-plan__tool"
              aria-pressed={showWallNumbers}
              onClick={() => setShowWallNumbers((on) => !on)}
              title="Show or hide each wall's number in its room — Wall 1, Wall 2 … as the tables and checks name them"
            >
              Wall numbers
            </button>

            {canEditDimensions ? (
            <>
            <button
              type="button"
              className="floor-plan__tool floor-plan__tool--primary"
              aria-pressed={measuring && measureKind === "line"}
              onClick={() => toggleMeasuring("line")}
            >
              {measuring && measureKind === "line" ? "Done" : "Add dimension"}
            </button>
            <button
              type="button"
              className="floor-plan__tool"
              aria-pressed={measuring && measureKind === "angle"}
              onClick={() => toggleMeasuring("angle")}
              title="Measure the angle between two legs: three clicks"
            >
              {measuring && measureKind === "angle" ? "Done" : "Angle"}
            </button>
            <button
              type="button"
              className="floor-plan__tool"
              aria-pressed={measuring && measureKind === "area"}
              onClick={() => toggleMeasuring("area")}
              title="Measure an area: click round it, then Enter"
            >
              {measuring && measureKind === "area" ? "Done" : "Area"}
            </button>
            </>
            ) : null}
            {mayComment ? (
              <button
                type="button"
                className="floor-plan__tool floor-plan__tool--comment"
                aria-pressed={commenting}
                onClick={toggleCommenting}
                title="Pin a comment where it applies: click the plan, then write it"
              >
                {commenting ? "Done" : "Comment"}
              </button>
            ) : null}
            {editor.available ? (
              <button
                type="button"
                className="floor-plan__tool floor-plan__tool--edit"
                aria-pressed={editor.editing}
                onClick={toggleEditing}
                title="Correct what the scan got wrong: move, resize, add and delete walls, doors and windows"
              >
                {editor.editing ? "Finish editing" : "Edit plan"}
              </button>
            ) : null}
            {orientationChoice?.onTurn ? (
              <button
                type="button"
                className="floor-plan__tool"
                data-testid="plan-rotate"
                onClick={() => orientationChoice.onTurn?.((orientation.quarterTurns + 1) % 4)}
                title={
                  "Turn the plan a quarter turn, to put the front of the building at the bottom along the X axis. " +
                  "Saved for everyone who opens this survey, and used by every export." +
                  (orientation.front === "door"
                    ? " (Now: the front is the side with the entrance door.)"
                    : orientation.front === "none"
                      ? " (No entrance door was found on an outside wall.)"
                      : "")
                }
              >
                Rotate 90°
              </button>
            ) : null}
            {/*
              * OFFERED EVERYWHERE, NOT ONLY IN FULL SCREEN.
              *
              * The side panel takes roughly a third of the frame, and embedded
              * in the survey layout that left the drawing about 480px on a
              * 1460px screen — small enough that a wall's own dimension label
              * overlapped its neighbour's and the plan could not be read at
              * all. Full screen was the only escape, which is a strange thing
              * to require of somebody who just wants to see the room they are
              * standing in.
              */}
            <button
              type="button"
              className="floor-plan__tool"
              aria-pressed={!panelHidden}
              onClick={() => setPanelHidden((hidden) => !hidden)}
              title="Show or hide the panel beside the plan: comments, design check and the editor"
            >
              {panelHidden ? "Show panel" : "Hide panel"}
            </button>
          </div>
        </div>

        {/* Zoom, map-style: bottom-right, above the scale bar, out of the
            way of the tools along the top. */}
        <div
          className="floor-plan__zoom"
          role="group"
          aria-label="Zoom"
          style={{
            position: "absolute",
            right: 12,
            bottom: 44,
            display: "flex",
            flexDirection: "column",
            zIndex: 3,
          }}
        >
          <button
            type="button"
            className="floor-plan__zoom-button"
            aria-label="Zoom in"
            title="Zoom in (+)"
            onClick={() => zoomBy(1.25)}
          >
            +
          </button>
          <button
            type="button"
            className="floor-plan__zoom-button floor-plan__zoom-button--fit"
            aria-label="Fit the plan to the view"
            title="Fit (0)"
            onClick={() => setZoom(IDENTITY_ZOOM)}
          >
            Fit
          </button>
          <button
            type="button"
            className="floor-plan__zoom-button"
            aria-label="Zoom out"
            title="Zoom out (−)"
            onClick={() => zoomBy(0.8)}
          >
            −
          </button>
        </div>

        {/* A save the server refused is taken back off the plan. Said on the
            drawing itself, not only in the side panel, where it could sit
            scrolled out of view while the pipe just seemed to vanish. */}
        {planObjects.error ? (
          <div
            role="alert"
            data-testid="plan-save-banner"
            style={{
              position: "absolute",
              top: 60,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 4,
              maxWidth: "min(92%, 520px)",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "8px 12px",
              borderRadius: 8,
              background: "#fdecea",
              border: "1px solid #e0a39b",
              color: "#8a1c12",
              fontSize: 13,
              lineHeight: 1.35,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
          >
            <span>
              {/* A colleague's change getting there first is not a failure
                  to put right: the plan already shows their version. */}
              <strong>{planObjects.conflict ? "Not saved." : "Not saved — taken off the plan."}</strong>{" "}
              {planObjects.error}
            </span>
            <button
              type="button"
              onClick={planObjects.dismissError}
              style={{
                background: "transparent",
                border: 0,
                color: "inherit",
                cursor: "pointer",
                fontSize: 13,
                textDecoration: "underline",
                padding: 0,
                flex: "none",
              }}
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {/* The same card the 3D view shows for the same selection. Hidden
            while a tool is in hand, when it would only be in the way. */}
        {toolActive ? null : (
          <SelectedReadout groups={drawnGroups} {...(selectedId ? { selectedId } : {})} />
        )}
      </div>

      {/* Always present, never toggled, and sized here rather than by what
          is in it. The drawing is fitted to the space this column leaves, so
          a panel that appeared on "Add dimension" re-fitted the plan under the
          cursor — the surveyor aimed at a corner and the corner moved before
          the click landed (found testing f686ec4). The Objects section's
          options change with the tool in hand; the column does not. */}
      <div
        className={`floor-plan__side floor-plan__side--${wide ? "side" : "below"}`}
        data-testid="plan-side"
        style={
          // `position: relative` makes this column the containing block of
          // the visually-hidden text inside it (absolutely positioned), so its
          // own scroll box clips it. Without it that text escaped to the plan's
          // wrapper, made the wrapper scrollable, and a click deep in the list
          // scrolled the whole drawing out of its frame.
          sideHidden
            ? { display: "none" }
            : wide
            ? {
                position: "relative",
                // Full screen has room to spare: a panel you can read at a glance.
                flex: fullScreen ? "0 0 clamp(280px, 24%, 380px)" : "0 0 clamp(220px, 32%, 280px)",
                minWidth: 0,
                minHeight: 0,
                overflowY: "auto",
              }
            : // A fixed height, not "auto": the hints' wording changes with the
              // tool's state, and a panel that grew or shrank with it re-fitted
              // the drawing above by a few pixels at the moment of aiming.
              {
                position: "relative",
                flex: "0 0 auto",
                height: "min(184px, 42%)",
                minHeight: 0,
                overflowY: "auto",
              }
        }
      >
        {/* Never a silent fall back to the raw scan: a colleague's
            corrections that could not be read would otherwise look undone. */}
        {edits?.state.status === "failed" ? (
          <p className="floor-plan__panel-error" role="alert" data-testid="plan-edits-load-failed">
            The plan&apos;s corrections could not be loaded, so this is the scan as captured and it cannot be
            edited until they are. {edits.state.loadError}{" "}
            <button type="button" className="floor-plan__link" onClick={edits.state.reload}>
              Try again
            </button>
          </p>
        ) : edits?.state.status === "unsupported" ? (
          <p className="floor-plan__panel-note" data-testid="plan-edits-unsupported">
            This server does not keep corrections to the plan, so it shows the scan as captured.
          </p>
        ) : null}
        {/* Once, right after the change it came with: what the correction
            did to the heat loss when a person's values were kept (and what
            to check), or why it could not follow. Gone at the next change or
            on dismissal; the heat loss section keeps any note still true. */}
        {edits?.state.heatLossNote ? (
          <div className="floor-plan__panel-note" role="status" data-testid="plan-edit-heat-loss-note">
            <p>
              {edits.state.heatLossNote.note}{" "}
              <button type="button" className="floor-plan__link" onClick={edits.state.dismissHeatLossNote}>
                Dismiss
              </button>
            </p>
            <ReviewNoteList
              notes={edits.state.heatLossNote.reviewNotes}
              className="floor-plan__review-notes"
              testId="plan-edit-review-notes"
            />
          </div>
        ) : null}
        {/* A change refused after "Finish editing" was pressed — most often
            a colleague's change getting there first, so the plan just moved
            to their version — is said here, since the edit panel is gone. */}
        {!editing && edits?.state.error ? (
          <p className="floor-plan__panel-error" role="alert" data-testid="plan-edit-error">
            {edits.state.error}{" "}
            <button type="button" className="floor-plan__link" onClick={edits.state.dismissError}>
              Dismiss
            </button>
          </p>
        ) : null}
        {editing && edits ? (
          <PlanEditPanel
            placement={wide ? "side" : "below"}
            editor={editor}
            layer={edits}
            rooms={visible}
            activeStory={activeStory}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : null}
        {commentsSource ? (
          <PlanCommentsPanel
            placement={wide ? "side" : "below"}
            state={planComments}
            viewer={commentViewer}
            commenting={commenting}
            draft={
              commentDraft
                ? {
                    about: commentDraft.about,
                    storeyLabel: stories.find((s) => s.story === commentDraft.pin.storey)?.label ?? storyLabel(commentDraft.pin.storey),
                  }
                : null
            }
            onPostDraft={postComment}
            onCancelDraft={() => setCommentDraft(null)}
            selectedId={selectedCommentId}
            onSelect={pickComment}
            showResolved={showResolved}
            onToggleResolved={() => setShowResolved((on) => !on)}
            aboutOf={(thread) => commentTargetLabel(thread.pin.target, savedRooms, planObjects.objects)}
            storeyLabelOf={storeyLabelOf}
          />
        ) : null}
        {imagesSource ? (
          <PlanImagePanel
            state={planImage}
            canEdit={canEditDimensions}
            scaling={scalePoints}
            onStartScale={() => setScalePoints([])}
            onCancelScale={() => setScalePoints(null)}
            onApplyScale={(realM) => {
              const [a, b] = scalePoints ?? [];
              if (a && b && planImage.image) planImage.update(rescaledPlacement(planImage.image, a, b, realM));
              setScalePoints(null);
            }}
          />
        ) : null}
        <PlanObjectsPanel
          status={planObjects.status}
          loadError={planObjects.loadError}
          onReload={planObjects.reload}
          tool={tool}
          onArm={armTool}
          runPoints={run.length}
          runLengthM={runLengthM(run)}
          objects={storeyObjects}
          allObjects={planObjects.objects}
          selectedId={selectedObjectId}
          onSelect={pickObject}
          onRotate={(id) => rotateObject(id, ROTATE_STEP_DEG)}
          onRemove={removeObject}
          onLabel={(id, label) => planObjects.update(id, { label })}
          onPipeSpec={(id, spec) => planObjects.update(id, { spec })}
          onFittingSpec={(id, spec) => planObjects.update(id, { spec })}
          fits={fits}
          onApplySuggestion={applySuggestion}
          onRadiatorSpec={changeRadiatorSpec}
          design={{
            state: design,
            result: designCheck,
            onPickRoom: pickRoom,
            onApplyUpgrade: applyUpgrade,
            onHeatPumpDesign: changeHeatPumpDesign,
            onPlacePosition: startPlacingPosition,
            placingPositionId: positionTool?.positionId ?? null,
            planEdited: edits ? heatLossPlanNotice(edits.state) : null,
            storeyLabels: storeyNames,
          }}
          error={planObjects.error}
          onDismissError={planObjects.dismissError}
          readOnly={!canEditObjects}
        />
        <PlanDimensionsPanel
          placement={wide ? "side" : "below"}
          measuring={measuring}
          measureKind={measureKind}
          pending={pending !== null}
          pendingPoints={pendingPoints.length}
          dimensions={mine}
          selectedId={selectedDimensionId}
          home={dims.home}
          moved={dims.moved}
          stranded={dims.stranded}
          error={dims.error}
          onDismissError={dims.dismissError}
          onSelect={pickDimension}
          onRemove={dropDimension}
          onLabel={(id, label) => dims.setLabel(id, label)}
          readOnly={!canEditDimensions}
        />
      </div>
    </div>
  );
}
